import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ProviderContactDatabase } from "../../../../types";
import usePostProviderContact from "../../api/usePostProviderContact";
import usePutProviderContact from "../../api/usePutProviderContact";
import { isSupabaseError } from "../../../../constants/supabaseErrors";
import { useState } from "react";
import { CIVILITIES, NO_CIVILITY } from "../../constants/civilities";

const formSchema = z.object({
  civility: z.preprocess((val) => val === NO_CIVILITY ? null : val, z.enum(CIVILITIES).nullable()),
  name: z.string().min(1, 'Ce champ est obligatoire'),
  role: z.string().min(1, 'Ce champ est obligatoire'),
  provider_id: z.string().min(1, 'Ce champ est obligatoire'),
  phone1: z.string().nullable(),
  phone2: z.string().nullable(),
  email: z.preprocess((val) => val === '' ? null : val, z.email('Format incorrect').nullable()),
});

type FormValues = z.input<typeof formSchema>;
type FormValuesOutput = z.infer<typeof formSchema>;

type Props = {
  providerId: string;
  contact?: ProviderContactDatabase;
  onClose: () => void;
}

export default function useAddUpdateContactModal(props: Props) {
  const { mutateAsync: postContact, isPending: isPosting } = usePostProviderContact(props.providerId);
  const { mutateAsync: updateContact, isPending: isUpdating } = usePutProviderContact(props.providerId);

  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<FormValues, unknown, FormValuesOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      civility: props.contact?.civility ?? NO_CIVILITY,
      name: props.contact?.name ?? "",
      provider_id: props.providerId,
      email: props.contact?.email ?? '',
      phone1: props.contact?.phone1 ?? '',
      phone2: props.contact?.phone2 ?? '',
      role: props.contact?.role ?? "",
    },
  });

  async function handleSubmit(data: FormValuesOutput) {
    setServerError(null);

    try {
      if (props.contact?.id) {
        await updateContact({
          contactId: props.contact.id,
          command: data,
        });
      } else {
        await postContact(data);
      }
      props.onClose();
    } catch (errors: unknown) {
      if (isSupabaseError(errors)) {
        setServerError(errors.message);

        return;
      }

      setServerError('Une erreur est survenue.')
    }
  }

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: isPosting || isUpdating,
    serverError,
  };
}
